import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Snackbar,
  SvgIcon,
} from '@mui/material';
import { Link } from 'react-router-dom';
import GitHubIcon from '@mui/icons-material/GitHub';
import KeyIcon from '@mui/icons-material/Key';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SummarizeIcon from '@mui/icons-material/Summarize';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { usePageTitle } from '@/hooks/usePageTitle';

const mcpConfig = `{
  "moemoe": {
    "type": "http",
    "url": "https://gbmrzpnasfxsewvxclun.supabase.co/functions/v1/mcp/mcp",
    "headers": {
      "Authorization": "Bearer \${MOEMOE_API_KEY}"
    }
  }
}`;

const features = [
  {
    icon: <SentimentSatisfiedAltIcon sx={{ fontSize: 40 }} />,
    title: '情绪分析',
    description: '实时追踪 60+ 财经博主的多空观点',
  },
  {
    icon: <SummarizeIcon sx={{ fontSize: 40 }} />,
    title: '每日摘要',
    description: '聚合市场观点，提供可行见解',
  },
  {
    icon: <PersonSearchIcon sx={{ fontSize: 40 }} />,
    title: 'KOL 追踪',
    description: '追踪博主持仓和分析逻辑',
  },
];

const mcpTools = [
  { name: 'get_ticker_sentiment', description: '获取股票情绪分析' },
  { name: 'get_daily_summary', description: '每日市场摘要' },
  { name: 'get_blogger_positions', description: '博主持仓追踪' },
  { name: 'list_bloggers', description: '可用博主列表' },
  { name: 'search_viewpoints', description: '搜索观点' },
  { name: 'list_tickers', description: '可用股票列表' },
];

const agentSkills = [
  { name: '/consensus', description: '多博主共识筛选' },
  { name: '/compare', description: '股票对比分析' },
  { name: '/hot-topics', description: '热门话题追踪' },
  { name: '/sentiment-shift', description: '情绪变化检测' },
  { name: '/stock-analysis', description: '单股深度报告' },
  { name: '/morning-briefing', description: '每日早报' },
];

export default function FociPage() {
  usePageTitle('FOCI - Claude Code 插件');
  const [copied, setCopied] = useState(false);

  const handleCopyConfig = async () => {
    await navigator.clipboard.writeText(mcpConfig);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box>
      {/* Section 1: Hero */}
      <Box id="hero" sx={{ textAlign: 'center', mb: 6, pt: 4 }}>
        <Typography
          variant="h1"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '2rem', md: '3.5rem' },
            background: 'linear-gradient(135deg, #00ff88 0%, #00d4ff 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
          }}
        >
          FOCI — Focus On Core Ideas
        </Typography>
        <Typography
          variant="h5"
          sx={{ color: 'text.secondary', mb: 2, fontWeight: 500 }}
        >
          AI Finance Partner
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: 'text.secondary', maxWidth: 700, mx: 'auto' }}
        >
          FOCI 提供 MCP Server 和 Agent Skills，为你习惯用的 AI Agent 提供实时中文财经 KOL 情绪数据，
          帮助你快速了解市场观点、追踪博主持仓、分析股票情绪。
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', mt: 1.5 }}
        >
          兼容 Claude Code、Cursor、Codex、Gemini CLI、Trae 等主流 Agent 工具
        </Typography>

        {/* CTA Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mt: 4 }}>
          <Button
            variant="outlined"
            size="large"
            startIcon={<GitHubIcon />}
            href="https://github.com/AlphaMoeMoe/FOCI/"
            target="_blank"
            rel="noopener noreferrer"
          >
            GitHub
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={
              <SvgIcon viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"
                />
              </SvgIcon>
            }
            href="https://discord.gg/HCWwpRQQ7J"
            target="_blank"
            rel="noopener noreferrer"
          >
            Discord
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={<KeyIcon />}
            component={Link}
            to="/api-keys"
          >
            获取 API Key
          </Button>
        </Box>
      </Box>

      {/* Section 2: Features */}
      <Box id="features" sx={{ mb: 6 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3, textAlign: 'center' }}>
          核心功能
        </Typography>
        <Grid container spacing={3}>
          {features.map((feature) => (
            <Grid xs={12} md={4} key={feature.title}>
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  textAlign: 'center',
                  bgcolor: '#131920',
                }}
              >
                <Box sx={{ color: 'primary.main', mb: 2 }}>
                  {feature.icon}
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Section 3: MCP Tools */}
      <Box id="mcp-tools" sx={{ mb: 6 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3, textAlign: 'center' }}>
          MCP Tools
        </Typography>
        <Paper sx={{ p: 3, bgcolor: '#131920' }}>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {mcpTools.map((tool) => (
              <Box component="li" key={tool.name} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                <Box
                  component="code"
                  sx={{
                    px: 1,
                    py: 0.5,
                    bgcolor: 'grey.900',
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    color: '#00D4AA',
                  }}
                >
                  {tool.name}
                </Box>
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 1.5 }}
                >
                  - {tool.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>

      {/* MCP Config */}
      <Box id="mcp-config" sx={{ mb: 6 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3, textAlign: 'center' }}>
          轻松配置 MCP
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
          把这段 config 加入到 Agent 工具的 MCP 设置中即可安装
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
          记得前往{' '}
          <Link to="/api-keys" style={{ color: '#00D4AA' }}>
            /api-keys
          </Link>{' '}
          获取自己的 API Key
        </Typography>
        <Paper sx={{ p: 0, bgcolor: '#131920', overflow: 'hidden', position: 'relative' }}>
          <Tooltip title={copied ? '已复制！' : '复制'}>
            <IconButton
              onClick={handleCopyConfig}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'grey.500',
                '&:hover': { color: 'grey.300' },
              }}
            >
              {copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
            </IconButton>
          </Tooltip>
          <Box
            component="pre"
            sx={{
              p: 2,
              pr: 5,
              m: 0,
              bgcolor: 'grey.900',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              color: 'grey.300',
              overflow: 'auto',
            }}
          >
            {mcpConfig}
          </Box>
        </Paper>
      </Box>

      {/* Section 4: Agent Skills */}
      <Box id="agent-skills" sx={{ mb: 6 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3, textAlign: 'center' }}>
          Agent Skills
        </Typography>
        <Grid container spacing={2}>
          {agentSkills.map((skill) => (
            <Grid xs={12} sm={6} md={4} key={skill.name}>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: '#131920',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box
                  component="code"
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    bgcolor: 'grey.900',
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    color: '#00D4AA',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {skill.name}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {skill.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Agent Skills Install */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
          一键安装
        </Typography>
        <Box
          component="pre"
          sx={{
            p: 2,
            m: 0,
            bgcolor: 'grey.900',
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            color: 'grey.300',
            textAlign: 'center',
          }}
        >
          npx skills install AlphaMoeMoe/FOCI
        </Box>
      </Box>

      {/* Section 5: Installation Guide */}
      <Box id="installation" sx={{ mb: 6 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3, textAlign: 'center' }}>
          Claude Code 安装指南
        </Typography>
        <Paper sx={{ p: 3, bgcolor: '#131920' }}>
          {/* Step 1 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
              1. 获取 API Key
            </Typography>
            <Typography variant="body2" color="text.secondary">
              前往{' '}
              <Link to="/api-keys" style={{ color: '#00D4AA' }}>
                API Keys 页面
              </Link>{' '}
              创建您的 API Key。
            </Typography>
          </Box>

          {/* Step 2 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
              2. 配置 Claude Code{' '}
              <a
                href="https://code.claude.com/docs/en/settings#settings-files"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#00D4AA' }}
              >
                settings.json
              </a>
            </Typography>
            <Box
              component="pre"
              sx={{
                p: 2,
                bgcolor: 'grey.900',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                color: 'grey.300',
                overflow: 'auto',
                m: 0,
              }}
            >
{`{
  "env": {
    "MOEMOE_API_KEY": "mm_your-api-key"
  }
}`}
            </Box>
          </Box>

          {/* Step 3 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
              3. 安装插件
            </Typography>
            <Box
              component="pre"
              sx={{
                p: 2,
                bgcolor: 'grey.900',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                color: 'grey.300',
                overflow: 'auto',
                m: 0,
              }}
            >
{`/plugin marketplace add AlphaMoeMoe/FOCI
/plugin install AlphaMoeMoe@foci`}
            </Box>
          </Box>

          {/* Step 4 */}
          <Box>
            <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
              4. 重启 Claude Code
            </Typography>
            <Typography variant="body2" color="text.secondary">
              重启后即可开始使用 FOCI 的所有功能。
            </Typography>
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="已复制到剪贴板！"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
